import React, { useState, useRef, useEffect, useContext, forwardRef, useImperativeHandle } from "react";
import { AppStateContext } from "../../state/AppProvider";
import { AnimatePresence, motion } from "framer-motion";
import { Spinner, ISpinnerStyles } from "@fluentui/react";
import { ButtonTooltip } from "../ButtonTooltip";
import { DOCUMENT_ACCEPT } from "../../constants/fileUpload";

interface Props {
    onSend: (question: string) => void;
    disabled: boolean;
    isGenerating: boolean;
    onStop: () => void;
    clearOnSend?: boolean;
    chatEmpty: boolean;
    windowWidth: number;
    fileInputRef?: React.RefObject<HTMLInputElement>;
    uploadFile?: () => void;
    showToEnd?: boolean;
    toEnd?: () => void;
    reasoning: boolean;
    setReasoning: (v: boolean) => void;
    webSearch: boolean;
    setWebSearch: (v: boolean) => void;
}

export type QuestionInputHandle = {
    restoreQuestion: (text: string) => void;
};

export const QuestionInput = forwardRef<QuestionInputHandle, Props>(({
    onSend,
    disabled,
    isGenerating,
    onStop,
    clearOnSend,
    chatEmpty,
    windowWidth,
    fileInputRef,
    uploadFile,
    showToEnd,
    toEnd,
    reasoning,
    setReasoning,
    webSearch,
    setWebSearch
}: Props, ref) => {
    const appStateContext = useContext(AppStateContext);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const plusButtonRef = useRef<HTMLButtonElement>(null);

    const placeholders = appStateContext?.state.assistant?.examples || [];
    const tools = appStateContext?.state.assistant?.tools || [];
    const hasReasoning = tools.includes("reasoning");
    const hasWebSearch = tools.includes("web_search");
    const hasAttachments = appStateContext?.state.assistant?.attachments;

    const [question, setQuestion] = useState<string>("");
    const [menuOpen, setMenuOpen] = useState(false);
    const [currentPlaceholder, setCurrentPlaceholder] = useState(0);

    // Restore a question after an early stop without clobbering a draft the
    // user typed while the request was in flight.
    useImperativeHandle(
        ref,
        () => ({
            restoreQuestion: (text: string) => {
                setQuestion(q => (q.trim() ? q : text));
                textAreaRef.current?.focus();
            }
        }),
        []
    );

    const activePills: { key: string; label: string; icon: string; onRemove: () => void }[] = [];
    if (webSearch) activePills.push({ key: "search", label: "Search", icon: "captive_portal", onRemove: () => setWebSearch(false) });
    if (reasoning) activePills.push({ key: "reasoning", label: "Thinking", icon: "psychology", onRemove: () => setReasoning(false) });

    const showPlusMenu = hasAttachments || hasReasoning || hasWebSearch;
    const hasPills = activePills.length > 0;

    const assistantId = appStateContext?.state.assistant?.assistant;
    useEffect(() => {
        setMenuOpen(false);
    }, [assistantId, hasReasoning, hasWebSearch, hasAttachments]);

    const calculateRows = (): void => {
        const textArea = textAreaRef.current;
        if (!textArea) return;
        textArea.style.height = "auto";
        textArea.style.height = `${textArea.scrollHeight}px`;
    };

    useEffect(() => {
        calculateRows();
    }, [textAreaRef?.current?.value]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(e.target as Node) &&
                plusButtonRef.current &&
                !plusButtonRef.current.contains(e.target as Node)
            ) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const sendQuestion = () => {
        if (disabled || !question.trim()) return;
        onSend(question);
        if (clearOnSend) setQuestion("");
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onQuestionChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        calculateRows();
        if (!ev.target.value) {
            setQuestion("");
            vanishAndSubmit();
        } else {
            setQuestion(ev.target.value);
        }
    };

    const sendQuestionDisabled = disabled || !question.trim();

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentPlaceholder(prev => (prev + 1) % placeholders.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [placeholders.length]);

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const newDataRef = useRef<any[]>([]);

    const animate = (start: number) => {
        const animateFrame = (pos: number = 0) => {
            requestAnimationFrame(() => {
                const newArr = [];
                for (let i = 0; i < newDataRef.current.length; i++) {
                    const current = newDataRef.current[i];
                    if (current.x < pos) {
                        newArr.push(current);
                    } else {
                        if (current.r <= 0) { current.r = 0; continue; }
                        current.x += Math.random() > 0.5 ? 1 : -1;
                        current.y += Math.random() > 0.5 ? 1 : -1;
                        current.r -= 0.05 * Math.random();
                        newArr.push(current);
                    }
                }
                newDataRef.current = newArr;
                const ctx = canvasRef.current?.getContext("2d");
                if (ctx) {
                    ctx.clearRect(pos, 0, 800, 800);
                    newDataRef.current.forEach(t => {
                        const { x: n, y: i, r: s, color } = t;
                        if (n > pos) {
                            ctx.beginPath();
                            ctx.rect(n, i, s, s);
                            ctx.fillStyle = color;
                            ctx.strokeStyle = color;
                            ctx.stroke();
                        }
                    });
                }
                if (newDataRef.current.length > 0) {
                    animateFrame(pos - 8);
                } else {
                    setQuestion("");
                }
            });
        };
        animateFrame(start);
    };

    const vanishAndSubmit = () => {
        const value = textAreaRef.current?.value || "";
        if (value && textAreaRef.current) {
            const maxX = newDataRef.current.reduce((prev, current) => (current.x > prev ? current.x : prev), 0);
            animate(maxX);
        }
    };

    const spinnerStyles: ISpinnerStyles = {
        circle: {
            borderColor: "#019dcd #019dcd #c2f1ff00 #c2f1ff00",
            height: "16px",
            width: "16px",
            borderWidth: "2px",
            borderStyle: "solid"
        }
    };

    const removeFile = (index: number) => {
        appStateContext?.dispatch({ type: "REMOVE_FILE", payload: index });
        if (fileInputRef?.current) fileInputRef.current.value = "";
    };

    const onUploadFileClick = () => {
        fileInputRef?.current?.click();
        setMenuOpen(false);
    };

    const menuPopup = (
        <AnimatePresence>
            {menuOpen && (
                <motion.div
                    ref={menuRef}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.075, ease: "easeOut" }}
                    className="absolute bottom-full -left-3 mb-1 w-44 rounded-md bg-jtc-white border border-jtc-grey z-50 overflow-hidden"
                >
                    {hasAttachments && (
                        <button
                            onClick={onUploadFileClick}
                            className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-jtc-very-dark-grey hover:bg-[rgba(75,75,75,0.05)]"
                        >
                            <span className="material-symbols-outlined text-[1rem] leading-none">attach_file</span>
                            <span>Attach files</span>
                        </button>
                    )}
                    {hasReasoning && (
                        <button
                            onClick={() => { setReasoning(!reasoning); setMenuOpen(false); }}
                            className="flex items-center justify-between w-full text-left px-3 py-2 text-sm text-jtc-very-dark-grey hover:bg-[rgba(75,75,75,0.05)]"
                        >
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-[1rem] leading-none">psychology</span>
                                <span>Thinking</span>
                            </div>
                            {reasoning && <span className="material-symbols-outlined text-[1rem] leading-none text-jtc-blue">check</span>}
                        </button>
                    )}
                    {hasWebSearch && (
                        <button
                            onClick={() => { setWebSearch(!webSearch); setMenuOpen(false); }}
                            className="flex items-center justify-between w-full text-left px-3 py-2 text-sm text-jtc-very-dark-grey hover:bg-[rgba(75,75,75,0.05)]"
                        >
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-[1rem] leading-none">captive_portal</span>
                                <span>Web search</span>
                            </div>
                            {webSearch && <span className="material-symbols-outlined text-[1rem] leading-none text-jtc-blue">check</span>}
                        </button>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );

    // Shared send button SVG
    const sendSvg = (
        <motion.svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-jtc-white h-4 w-4"
        >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <motion.path
                d="M5 12l14 0"
                initial={{ strokeDasharray: "50%", strokeDashoffset: "50%" }}
                animate={{ strokeDashoffset: !sendQuestionDisabled ? 0 : "50%" }}
                transition={{ duration: 0.3, ease: "linear" }}
            />
            <path d="M13 18l6 -6" />
            <path d="M13 6l6 6" />
        </motion.svg>
    );

    return (
        <div
            className={
                "transition-[padding] duration-200 text-base m-auto pl-[20px] pr-[29px] " +
                (appStateContext?.state.isChatHistoryOpen ? "" : "lg:pl-[20px] lg:pr-[29px]")
            }
        >
            {showToEnd && (
                <div
                    className="absolute left-0 right-0 z-[4] mx-auto w-[32px] h-[32px] animate-bounce rounded-full bg-jtc-white outline outline-2 outline-jtc-purple hover:outline-jtc-blue"
                    style={{ bottom: textAreaRef.current ? textAreaRef.current.scrollHeight + 40 : 99 }}
                >
                    <button className="flex justify-center items-center h-[32px] w-[32px] font-semibold text-jtc-purple hover:text-jtc-blue" onClick={toEnd}>
                        <span className="material-symbols-outlined text-[1.25rem]">keyboard_arrow_down</span>
                    </button>
                </div>
            )}

            <div className="mx-auto flex flex-1 text-base gap-4 lg:gap-6 lg:max-w-chat-composer">
                <div className="flex w-full items-center">
                    {appStateContext?.state.assistant?.assistant === "sp" && windowWidth >= 976 && (
                        <a
                            className="mr-6 max-w-sm min-w-72 flex-grow bg-jtc-white hover:text-jtc-white flex items-center justify-center h-[59px] rounded-lg"
                            href="https://joogle.jtcgroup.com/shared-ownership/tax-guidance/"
                            target="_blank"
                            style={{ flexBasis: "60%" }}
                        >
                            <div className="tracking-[0.15rem] text-jtc-purple hover:text-jtc-blue cursor-pointer font-semibold">
                                Click here for tax guidance
                            </div>
                        </a>
                    )}

                    <div
                        key={assistantId || "default"}
                        className={
                            "flex flex-col w-full flex-grow-1 relative rounded-lg bg-jtc-grey " +
                            (showPlusMenu ? "overflow-visible" : "overflow-hidden")
                        }
                    >
                        {/* File chips */}
                        {hasAttachments &&
                            appStateContext?.state.files &&
                            appStateContext.state.files.length > 0 &&
                            appStateContext.state.files.map((fileItem, index) =>
                                fileItem.new ? (
                                    <a key={index} className="gap-2 w-fit flex pl-3.5 pt-3.5 pb-1 items-center">
                                        {fileItem.status === "Loading" && (
                                            <span className="flex justify-center items-center h-[24px] w-[24px]">
                                                <Spinner style={{ display: "block", verticalAlign: "middle" }} styles={spinnerStyles} />
                                            </span>
                                        )}
                                        <span
                                            className={fileItem.status === "Error" ? "text-jtc-disabled" : "text-jtc-black"}
                                            title={fileItem.status === "Error" ? fileItem.error : undefined}
                                        >
                                            {fileItem.status === "Error"
                                                ? `${fileItem.file.name}: ${fileItem.error ?? "Unable to upload"}`
                                                : fileItem.file.name}
                                        </span>
                                        <ButtonTooltip title="Remove file" placement="top">
                                            <button className="flex justify-center font-semibold text-jtc-blue" onClick={() => removeFile(index)}>
                                                <span className="material-symbols-outlined text-[1.25rem]">cancel</span>
                                            </button>
                                        </ButtonTooltip>
                                    </a>
                                ) : null
                            )}

                        {/* Hidden file input */}
                        {hasAttachments && (
                            <input
                                className="hidden"
                                type="file"
                                accept={DOCUMENT_ACCEPT}
                                ref={fileInputRef}
                                onChange={uploadFile}
                                multiple
                                maxLength={2}
                            />
                        )}

                        {/*
                            COLLAPSED STATE (no pills):
                            Textarea, +, send, placeholder all on the main container.
                        */}
                        {!hasPills && (
                            <>
                                <textarea
                                    ref={textAreaRef}
                                    className={
                                        "m-0 w-full resize-none border-0 bg-transparent outline-none focus:outline-none focus:ring-0 focus-visible:ring-0 py-3.5 pr-28 max-h-[200px] min-h-[52px] text-jtc-black placeholder-[#7F7F7F]" +
                                        (showPlusMenu ? " pl-12" : " pl-4")
                                    }
                                    rows={1}
                                    placeholder={!chatEmpty ? "Send a message" : ""}
                                    value={question}
                                    onChange={onQuestionChange}
                                    onKeyDown={onEnterPress}
                                />

                                {/* + button */}
                                {showPlusMenu && (
                                    <div className="absolute bottom-[17px] left-3 h-[28px] z-10">
                                        <div className="relative">
                                            <ButtonTooltip title={menuOpen ? "" : "More options"} placement="bottom">
                                                <button
                                                    ref={plusButtonRef}
                                                    onClick={() => !disabled && setMenuOpen(!menuOpen)}
                                                    className={
                                                        "h-7 w-7 rounded-full flex items-center justify-center transition-all duration-200 " +
                                                        (disabled
                                                            ? "text-jtc-disabled cursor-default"
                                                            : menuOpen
                                                                ? "bg-jtc-purple text-jtc-white"
                                                                : "text-[#7F7F7F] hover:bg-jtc-grey")
                                                    }
                                                >
                                                    <motion.span
                                                        className="material-symbols-outlined text-[1.25rem]"
                                                        animate={{ rotate: menuOpen ? 45 : 0 }}
                                                        transition={{ duration: 0.15 }}
                                                    >
                                                        add
                                                    </motion.span>
                                                </button>
                                            </ButtonTooltip>
                                            {menuPopup}
                                        </div>
                                    </div>
                                )}

                                {/* Send / stop button */}
                                {isGenerating ? (
                                    <ButtonTooltip title="Stop generating" placement="top">
                                        <button
                                            aria-label="Stop generating"
                                            onClick={onStop}
                                            className="absolute right-3 bottom-[11.5px] z-50 h-8 w-8 rounded-lg bg-jtc-purple transition duration-200 flex items-center justify-center"
                                        >
                                            <span
                                                className="material-symbols-outlined text-[1.25rem] leading-none text-jtc-white"
                                                style={{ fontVariationSettings: "'FILL' 1" }}
                                            >
                                                stop
                                            </span>
                                        </button>
                                    </ButtonTooltip>
                                ) : (
                                    <button
                                        disabled={sendQuestionDisabled}
                                        onClick={sendQuestion}
                                        type="submit"
                                        className="absolute right-3 bottom-[11.5px] z-50 h-8 w-8 rounded-lg disabled:bg-jtc-disabled bg-jtc-purple transition duration-200 flex items-center justify-center"
                                    >
                                        {sendSvg}
                                    </button>
                                )}

                                {/* Rotating placeholder */}
                                {!question && chatEmpty && (
                                    <div className="absolute flex items-center rounded-lg pointer-events-none h-[59px] bottom-0 left-0 right-0">
                                        <AnimatePresence mode="wait">
                                            <motion.p
                                                initial={{ y: 5, opacity: 0 }}
                                                key={`current-placeholder-${currentPlaceholder}`}
                                                animate={{ y: 0, opacity: 1 }}
                                                exit={{ y: -15, opacity: 0 }}
                                                transition={{ duration: 0.3, ease: "linear" }}
                                                className={
                                                    "text-left w-[calc(100%-2rem)] truncate text-[#7F7F7F]" +
                                                    (showPlusMenu ? " pl-12" : " pl-4")
                                                }
                                            >
                                                {placeholders[currentPlaceholder]}
                                            </motion.p>
                                        </AnimatePresence>
                                    </div>
                                )}
                            </>
                        )}

                        {/*
                            EXPANDED STATE (pills active): textarea with relative wrapper,
                            then bottom bar with +, pills, send.
                        */}
                        {hasPills && (
                            <>
                                <div className="relative">
                                    <textarea
                                        ref={textAreaRef}
                                        className="m-0 w-full resize-none border-0 bg-transparent outline-none focus:outline-none focus:ring-0 focus-visible:ring-0 pt-[1rem] pb-3 pl-4 pr-14 max-h-[200px] min-h-[52px] leading-6 text-jtc-black placeholder-[#7F7F7F]"
                                        rows={1}
                                        placeholder={!chatEmpty ? "Send a message" : ""}
                                        value={question}
                                        onChange={onQuestionChange}
                                        onKeyDown={onEnterPress}
                                    />

                                    {/* Rotating placeholder */}
                                    {!question && chatEmpty && (
                                        <div className="absolute inset-0 pointer-events-none pt-[1rem] pb-3 pl-4 pr-14">
                                            <AnimatePresence mode="wait">
                                                <motion.p
                                                    initial={{ y: 5, opacity: 0 }}
                                                    key={`current-placeholder-${currentPlaceholder}`}
                                                    animate={{ y: 0, opacity: 1 }}
                                                    exit={{ y: -15, opacity: 0 }}
                                                    transition={{ duration: 0.3, ease: "linear" }}
                                                    className="text-left w-full truncate text-[#7F7F7F] leading-6"
                                                >
                                                    {placeholders[currentPlaceholder]}
                                                </motion.p>
                                            </AnimatePresence>
                                        </div>
                                    )}
                                </div>

                                {/* Bottom bar with +, pills, send */}
                                <div className="flex items-center justify-between px-2.5 pb-2.5 pt-0 overflow-visible">
                                    <div className="flex items-center gap-1.5">
                                        <div className="relative">
                                            <ButtonTooltip title={menuOpen ? "" : "More options"} placement="bottom">
                                                <button
                                                    ref={plusButtonRef}
                                                    onClick={() => !disabled && setMenuOpen(!menuOpen)}
                                                    className={
                                                        "h-8 w-8 rounded-full flex items-center justify-center transition-all duration-200 " +
                                                        (disabled
                                                            ? "text-jtc-disabled cursor-default"
                                                            : menuOpen
                                                                ? "bg-jtc-purple text-jtc-white"
                                                                : "text-[#7F7F7F] hover:bg-jtc-grey")
                                                    }
                                                >
                                                    <motion.span
                                                        className="material-symbols-outlined text-[1.25rem]"
                                                        animate={{ rotate: menuOpen ? 45 : 0 }}
                                                        transition={{ duration: 0.15 }}
                                                    >
                                                        add
                                                    </motion.span>
                                                </button>
                                            </ButtonTooltip>
                                            {menuPopup}
                                        </div>

                                        <AnimatePresence>
                                            {activePills.map(pill => (
                                                <motion.button
                                                    key={pill.key}
                                                    initial={{ opacity: 0, scale: 0.85, width: 0 }}
                                                    animate={{ opacity: 1, scale: 1, width: "auto" }}
                                                    exit={{ opacity: 0, scale: 0.85, width: 0 }}
                                                    transition={{ duration: 0.15 }}
                                                    onClick={pill.onRemove}
                                                    className="flex items-center gap-1.5 pl-2.5 pr-2 py-1 rounded-full bg-jtc-blue/10 text-jtc-blue text-xs font-medium hover:bg-jtc-blue/20 transition-colors whitespace-nowrap overflow-hidden"
                                                >
                                                    <span className="material-symbols-outlined text-[0.875rem]">{pill.icon}</span>
                                                    {pill.label}
                                                    <span className="material-symbols-outlined text-[0.875rem] opacity-60">close</span>
                                                </motion.button>
                                            ))}
                                        </AnimatePresence>
                                    </div>

                                    {isGenerating ? (
                                        <ButtonTooltip title="Stop generating" placement="top">
                                            <button
                                                aria-label="Stop generating"
                                                onClick={onStop}
                                                className="h-8 w-8 rounded-lg bg-jtc-purple transition duration-200 flex items-center justify-center flex-shrink-0"
                                            >
                                                <span
                                                    className="material-symbols-outlined text-[1.25rem] leading-none text-jtc-white"
                                                    style={{ fontVariationSettings: "'FILL' 1" }}
                                                >
                                                    stop
                                                </span>
                                            </button>
                                        </ButtonTooltip>
                                    ) : (
                                        <button
                                            disabled={sendQuestionDisabled}
                                            onClick={sendQuestion}
                                            type="submit"
                                            className="h-8 w-8 rounded-lg disabled:bg-jtc-disabled bg-jtc-purple transition duration-200 flex items-center justify-center flex-shrink-0"
                                        >
                                            {sendSvg}
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="relative px-2 py-2 text-center text-[0.667rem] text-jtc-white leading-normal">
                <span className="lg:hidden inline-block">JTC accepts no liability for the accuracy, completeness or use of ChatJTC's responses.</span>
                <span className="hidden lg:inline-block lg:max-w-chat-composer">
                    ChatJTC, powered by OpenAI, provides information and assistance for JTC users but cannot guarantee accuracy, completeness, or non-bias, so
                    users should verify responses with reliable sources. JTC accepts no liability for the accuracy, completeness or use of ChatJTC's responses.
                </span>
            </div>
        </div>
    );
});
